import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocaleStore } from '@/lib/core/stores';
import { ProfileAddressesNewEdit } from '@/components/shared/profile/adresses/new-edit';
import { SpinLoading } from '@/components/shared/loadings/spin';
import {
  createAddress,
  deleteAddress,
  getAddresses,
  updateAddress,
} from '@/services/platform/data/addresses';
import type { AddressBookDto } from '@/services/platform/api/model';

/**
 * Add/edit-address screen used inside the checkout flow. Reuses the same
 * form (`ProfileAddressesNewEdit`) and data layer as the profile address
 * book, so create/update/delete behave identically in both places.
 */
export function ShopAddAddressScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const locale = useLocaleStore(state => state.locale);

  const editingId = new URLSearchParams(location.search).get('id') ?? undefined;
  const [initial, setInitial] = useState<AddressBookDto | undefined>(undefined);
  const [isLoadingInitial, setIsLoadingInitial] = useState(!!editingId);

  useEffect(() => {
    if (!editingId) {
      setIsLoadingInitial(false);
      return;
    }
    let cancelled = false;
    setIsLoadingInitial(true);
    getAddresses(locale).then(addresses => {
      if (cancelled) return;
      setInitial(addresses?.find(address => address.id === editingId));
      setIsLoadingInitial(false);
    });
    return () => {
      cancelled = true;
    };
  }, [editingId, locale]);

  const goBack = () => navigate(-1);

  if (isLoadingInitial) {
    return <SpinLoading wrapperClassName="min-h-[50vh]" />;
  }

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white pb-[calc((env(safe-area-inset-bottom)+1.5rem)+48px)]">
      <ProfileAddressesNewEdit
        pageProperties={undefined}
        initialValues={initial}
        onBack={goBack}
        onSave={async data => {
          if (editingId) {
            await updateAddress(editingId, data, locale);
          } else {
            await createAddress(data, locale);
          }
          goBack();
        }}
        onDelete={
          editingId
            ? async () => {
                await deleteAddress(editingId);
                goBack();
              }
            : undefined
        }
      />
    </div>
  );
}
